import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

/* ============================================================
   STYLES (Floating Pill Nav Design)
============================================================ */
const CSS = `
:root {
  --header-h: 80px;
  --brand: #fd8110;
  --brand-glow: rgba(253, 129, 16, 0.45);
  --white: #ffffff;
  --muted: rgba(255, 255, 255, 0.65);
  --glass-bg: rgba(12, 12, 18, 0.7);
  --glass-border: rgba(255, 255, 255, 0.12);
  --dropdown-bg: rgba(14, 14, 22, 0.95);
  --nested-dropdown-bg: rgba(10, 10, 16, 0.97);
}

/* ---- Header Shell ---- */
#slh-header {
  position: fixed;
  inset: 0 0 auto 0;
  height: var(--header-h);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 3rem;
  background: black;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  font-family: 'Outfit', sans-serif;
  box-sizing: border-box;
}

#slh-header.scrolled {
  background: rgba(6, 6, 10, 0.85);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid var(--glass-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

/* ---- Logo ---- */
.slh-logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  flex-shrink: 0;
  transition: transform 200ms ease;
}

.slh-logo:hover {
  transform: translateY(-1px);
}

.slh-logo-img {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid var(--brand);
  object-fit: contain;
  flex-shrink: 0;
  box-shadow: 0 0 12px var(--brand-glow);
}

.slh-logo-text {
  line-height: 1.2;
}

.slh-logo-name {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: var(--white);
  letter-spacing: -0.01em;
}

.slh-logo-sub {
  display: block;
  font-size: 9.5px;
  font-weight: 600;
  color: var(--brand);
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/* ---- Floating Pill Nav (Center) ---- */
.slh-nav-pill-wrapper {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 9999px;
  padding: 4px 6px;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.slh-nav {
  display: flex;
  align-items: center;
  gap: 0.2rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.slh-nav-item {
  position: relative;
  display: flex;
  align-items: center;
}

.slh-nav-link {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 1.15rem;
  color: var(--muted);
  text-decoration: none;
  font-size: 13.5px;
  font-weight: 500;
  transition: all 200ms ease;
  cursor: pointer;
  background: none;
  border: none;
  border-radius: 9999px;
  white-space: nowrap;
}

.slh-nav-link:hover {
  color: var(--white);
  background: rgba(255, 255, 255, 0.06);
}

/* Active State Pill Styling */
.slh-nav-item.active > .slh-nav-link {
  color: var(--white);
  background: rgba(255, 255, 255, 0.14);
  font-weight: 600;
  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.2);
}

.slh-chevron {
  width: 12px;
  height: 12px;
  transition: transform 250ms ease, opacity 250ms ease;
  opacity: 0.6;
  flex-shrink: 0;
}

.slh-nav-item:hover > .slh-nav-link .slh-chevron {
  transform: rotate(180deg);
  opacity: 1;
}

/* ---- Action Button (Right Pill) ---- */
.slh-cta-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.4rem;
  background: #ffffff;
  color: #000000;
  text-decoration: none;
  font-size: 13.5px;
  font-weight: 600;
  border-radius: 9999px;
  transition: all 200ms ease;
  box-shadow: 0 2px 10px rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.slh-cta-button:hover {
  background: #f0f0f0;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(255, 255, 255, 0.3);
}

/* ---- Dropdowns ---- */
.slh-dropdown {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%) translateY(8px);
  min-width: 230px;
  background: var(--dropdown-bg);
  border: 1px solid var(--glass-border);
  border-radius: 16px;
  padding: 0.5rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms ease, transform 200ms ease;
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  box-shadow: 0 15px 35px rgba(0,0,0,0.6);
}

.slh-nav-item:hover .slh-dropdown {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}

.slh-service-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 10px;
  transition: background 180ms ease;
}

.slh-service-item:hover {
  background: rgba(255, 255, 255, 0.06);
}

.slh-service-main-link {
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding: 0.7rem 1rem;
  text-decoration: none;
  color: rgba(255,255,255,0.85);
  font-size: 13.5px;
  font-weight: 500;
}

.slh-service-chevron-box {
  display: flex;
  align-items: center;
  padding-right: 0.8rem;
}

.slh-service-chevron {
  width: 12px;
  height: 12px;
  opacity: 0.4;
  color: white;
  transition: all 200ms ease;
}

.slh-service-item:hover .slh-service-chevron {
  opacity: 1;
  color: var(--brand);
  transform: translateX(2px);
}

.slh-nested-dropdown {
  position: absolute;
  top: 0;
  left: calc(100% + 8px);
  min-width: 240px;
  background: var(--nested-dropdown-bg);
  border: 1px solid var(--glass-border);
  border-radius: 14px;
  padding: 0.5rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 200ms ease, transform 200ms ease;
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  box-shadow: 0 15px 35px rgba(0,0,0,0.65);
  transform: translateX(-6px);
}

.slh-service-item:hover .slh-nested-dropdown {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(0);
}

.slh-nested-item {
  display: flex;
  align-items: center;
  padding: 0.6rem 0.8rem;
  border-radius: 8px;
  text-decoration: none;
  color: rgba(255,255,255,0.78);
  font-size: 13px;
  font-weight: 500;
  transition: all 180ms ease;
}

.slh-nested-item:hover {
  background: rgba(253, 129, 16, 0.12);
  color: var(--brand);
}

.slh-nested-label small {
  display: block;
  font-size: 10px;
  color: rgba(255,255,255,0.45);
  margin-top: 2px;
}

/* ---- Mobile Menu / Hamburger ---- */
.slh-hamburger-btn {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-radius: 9999px;
  cursor: pointer;
  padding: 0;
}

.slh-hamburger-btn span {
  display: block;
  width: 18px;
  height: 2px;
  background: var(--white);
  border-radius: 2px;
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
}

.slh-hamburger-btn.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.slh-hamburger-btn.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
.slh-hamburger-btn.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* Mobile Drawer */
.slh-mobile-drawer {
  display: none;
  position: fixed;
  inset: var(--header-h) 0 0 0;
  background: rgba(6, 6, 10, 0.96);
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  z-index: 998;
  overflow-y: auto;
  padding: 1.5rem 1.5rem 3rem;
  transform: translateX(100%);
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

.slh-mobile-drawer.open {
  transform: translateX(0);
}

.slh-m-plain-link {
  display: block;
  padding: 0.85rem 1rem;
  color: rgba(255,255,255,0.8);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  border-radius: 12px;
}

.slh-m-plain-link.active {
  color: var(--white);
  background: rgba(255, 255, 255, 0.08);
  border-left: 3px solid var(--brand);
}

.slh-m-divider {
  height: 1px;
  background: var(--glass-border);
  margin: 1rem 0;
}

.slh-m-accordion-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255,255,255,0.03);
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.06);
}

.slh-m-accordion-main-link {
  flex-grow: 1;
  padding: 0.85rem 1rem;
  color: rgba(255,255,255,0.9);
  text-decoration: none;
  font-size: 14px;
}

.slh-m-accordion-chevron-toggle {
  padding: 0.85rem 1rem;
  cursor: pointer;
  color: rgba(255,255,255,0.85);
}

.slh-m-accordion-chevron {
  transition: transform 250ms ease;
}

.slh-m-accordion-chevron.open {
  transform: rotate(180deg);
  color: var(--brand);
}

.slh-m-accordion-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 320ms ease;
  padding-left: 0.5rem;
}

.slh-m-accordion-content.open {
  max-height: 550px;
}

.slh-m-nested-link {
  display: block;
  padding: 0.7rem 0.85rem;
  color: rgba(255,255,255,0.68);
  text-decoration: none;
  font-size: 13px;
  border-radius: 10px;
}

/* ---- Responsive Breakpoints ---- */
@media (max-width: 1024px) {
  #slh-header { padding: 0 1.5rem; }
  .slh-nav-link { padding: 0.5rem 0.85rem; font-size: 12.5px; }
}

@media (max-width: 920px) {
  .slh-nav-pill-wrapper { display: none; }
  .slh-cta-button { display: none; }
  .slh-hamburger-btn { display: flex; }
  .slh-mobile-drawer { display: block; }
}

@media (max-width: 480px) {
  #slh-header { padding: 0 1rem; }
  .slh-logo-img { width: 36px; height: 36px; }
  .slh-logo-name { font-size: 14px; }
  .slh-logo-sub { font-size: 8px; }
}
`;

/* ============================================================
   DATA CONFIGURATIONS
============================================================ */
const SERVICE_SUB_ITEMS = [
  { label: "Account Creation", desc: "Setup & verification", slug: "account-creation" },
  { label: "Account Handling", desc: "Full operations management", slug: "account-handling" },
  { label: "Account Scalability", desc: "Scale business volumes", slug: "account-scalability" },
  { label: "Account Hunting", desc: "Find optimized accounts", slug: "account-hunting" },
  { label: "Account Listing", desc: "Product entry & optimization", slug: "account-listing" },
  { label: "Order Processing", desc: "Fulfillment workflows", slug: "order-processing" },
  { label: "Order Tracking", desc: "Logistics tracking management", slug: "order-tracking" },
];

const SERVICES_DATA = [
  { id: "tiktok-shop", label: "TikTok Shop", routePrefix: "/services/tiktok-shop" },
  { id: "shopify", label: "Shopify", routePrefix: "/services/shopify" },
  { id: "ebay", label: "eBay", routePrefix: "/services/ebay" },
];

const LINKS_BEFORE_SERVICES = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about-us" },
  
];

const LINKS_AFTER_SERVICES = [
  { label: "Blog", to: "https://blogs.softbecklearninghub.com/" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Internships", to: "/internships" },
];

/* ============================================================
   COMPONENT
============================================================ */
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobileService, setOpenMobileService] = useState(null);
  const styleInjected = useRef(false);
  const location = useLocation();

  useEffect(() => {
    if (styleInjected.current) return;
    styleInjected.current = true;

    const tag = document.createElement("style");
    tag.textContent = CSS;
    document.head.appendChild(tag);
  }, []);

  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 15);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 920) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);

  const toggleMobileService = useCallback((serviceId) => {
    setOpenMobileService((prev) => (prev === serviceId ? null : serviceId));
  }, []);

  const getServiceRoute = (service, subItemSlug) => {
    return `${service.routePrefix}/${subItemSlug}`;
  };

  return (
    <>
      <header id="slh-header" className={scrolled ? "scrolled" : ""}>
        {/* Left Branding Area */}
        <Link to="/" className="slh-logo">
          <img 
            src="/icon.jpg" 
            alt="Softbeck Logo" 
            width={42}
            height={42}
            className="slh-logo-img"
            decoding="async"
          />
          <div className="slh-logo-text">
            <span className="slh-logo-name">Softbeck</span>
            <span className="slh-logo-sub">Learning Hub</span>
          </div>
        </Link>

        {/* Centered Floating Pill Navigation */}
        <div className="slh-nav-pill-wrapper">
          <ul className="slh-nav" role="navigation" aria-label="Main navigation">
            {LINKS_BEFORE_SERVICES.map((pl) => (
              <li key={pl.to} className={`slh-nav-item${location.pathname === pl.to ? " active" : ""}`}>
                <Link to={pl.to} className="slh-nav-link">
                  {pl.label}
                </Link>
              </li>
            ))}

            {/* Services Dropdown */}
            <li className={`slh-nav-item${location.pathname.startsWith("/services") ? " active" : ""}`}>
              <button className="slh-nav-link" aria-haspopup="true">
                Services
                <svg className="slh-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              <div className="slh-dropdown" role="menu">
                {SERVICES_DATA.map((service) => (
                  <div key={service.id} className="slh-service-item">
                    <Link to={service.routePrefix} className="slh-service-main-link">
                      <span>{service.label}</span>
                    </Link>

                    <div className="slh-service-chevron-box">
                      <svg className="slh-service-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </div>

                    <div className="slh-nested-dropdown">
                      {SERVICE_SUB_ITEMS.map((item, idx) => (
                        <Link key={idx} to={getServiceRoute(service, item.slug)} className="slh-nested-item">
                          <div className="slh-nested-label">
                            {item.label}
                            <small>{item.desc}</small>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </li>

            {LINKS_AFTER_SERVICES.map((pl) => (
              <li key={pl.to} className={`slh-nav-item${location.pathname === pl.to ? " active" : ""}`}>
                <Link to={pl.to} className="slh-nav-link">
                  {pl.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Right Action Button */}
        <Link to="/contact" className="slh-cta-button">
          Contact Us
        </Link>

        {/* Hamburger Mobile Button */}
        <button
          className={`slh-hamburger-btn${mobileOpen ? " open" : ""}`}
          onClick={toggleMobile}
          aria-label="Toggle navigation"
          aria-expanded={mobileOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      {/* Mobile Drawer */}
      <div className={`slh-mobile-drawer${mobileOpen ? " open" : ""}`} aria-hidden={!mobileOpen}>
        <div className="slh-m-section">
          {LINKS_BEFORE_SERVICES.map((pl) => (
            <Link
              key={pl.to}
              to={pl.to}
              className={`slh-m-plain-link${location.pathname === pl.to ? " active" : ""}`}
              onClick={() => setMobileOpen(false)}
            >
              {pl.label}
            </Link>
          ))}
        </div>

        <div className="slh-m-divider" />

        <div className="slh-m-section">
          {SERVICES_DATA.map((service) => (
            <div key={service.id} className="slh-m-accordion">
              <div className="slh-m-accordion-flex">
                <Link 
                  to={service.routePrefix}
                  className="slh-m-accordion-main-link"
                  onClick={() => setMobileOpen(false)}
                >
                  {service.label}
                </Link>

                <div 
                  className="slh-m-accordion-chevron-toggle"
                  onClick={() => toggleMobileService(service.id)}
                >
                  <svg
                    className={`slh-m-accordion-chevron ${openMobileService === service.id ? "open" : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </div>
              </div>

              <div className={`slh-m-accordion-content ${openMobileService === service.id ? "open" : ""}`}>
                {SERVICE_SUB_ITEMS.map((item, idx) => (
                  <Link
                    key={idx}
                    to={getServiceRoute(service, item.slug)}
                    className="slh-m-nested-link"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div>
                      {item.label}
                      <small style={{ display: "block", fontSize: "10px", opacity: 0.5, marginTop: "2px" }}>{item.desc}</small>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="slh-m-divider" />

        <div className="slh-m-section">
          {LINKS_AFTER_SERVICES.map((pl) => (
            <Link
              key={pl.to}
              to={pl.to}
              className={`slh-m-plain-link${location.pathname === pl.to ? " active" : ""}`}
              onClick={() => setMobileOpen(false)}
            >
              {pl.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="slh-m-plain-link"
            style={{ marginTop: "1rem", background: "#ffffff", color: "#000000", textAlign: "center" }}
            onClick={() => setMobileOpen(false)}
          >
            Contact Us
          </Link>
        </div>
      </div>
    </>
  );
}